import clsx from 'clsx';

export interface UnitMixRow {
  label: string;
  count: number;
  /** Tailwind background token — see `<CapitalStackBar>` for the convention. */
  color: string;
}

export interface UnitMixBarProps {
  rows: UnitMixRow[];
  /** Compact mode renders a thicker bar with in-segment percentages + denser legend. */
  compact?: boolean;
  className?: string;
}

const FALLBACK_COLOR = 'bg-text-muted';
const MIN_VISIBLE_PCT = 0.02;

/**
 * Horizontal stacked-bar of unit-mix proportions. Sibling of
 * `<CapitalStackBar>` — same primitive shape, different domain.
 *
 * The blueprint distinguishes them as separate primitives (rather than one
 * generic `<StackedBar>`) because the labels + caption legend phrasing
 * differ semantically — Capital Stack reports dollar amounts, Unit Mix
 * reports unit counts. Having distinct primitives keeps the variant
 * components more legible at the call site.
 */
export function UnitMixBar({ rows, compact = false, className }: UnitMixBarProps) {
  const total = rows.reduce(
    (sum, r) => sum + (Number.isFinite(r.count) ? r.count : 0),
    0,
  );
  const isEmpty = rows.length === 0 || total <= 0;
  const barHeight = compact ? 'h-[22px]' : 'h-3';

  return (
    <div
      data-testid="unit-mix-bar"
      data-total={total}
      data-empty={isEmpty ? 'true' : 'false'}
      className={clsx('flex flex-col gap-1.5', className)}
    >
      <div className={clsx('flex rounded-[2px] overflow-hidden border border-panel-border', barHeight)}>
        {isEmpty ? (
          <div className="flex-1 bg-panel-bg" aria-hidden="true" />
        ) : (
          rows.map((row, i) => {
            const rawPct = row.count / total;
            const widthPct = Math.max(rawPct, MIN_VISIBLE_PCT) * 100;
            const showInBarPct = compact && widthPct >= 12;
            return (
              <div
                key={`${row.label}-${i}`}
                data-row-label={row.label}
                data-row-count={row.count}
                className={clsx(row.color || FALLBACK_COLOR, 'h-full flex items-center justify-center')}
                style={{ width: `${widthPct}%` }}
                title={`${row.label}: ${row.count} units`}
              >
                {showInBarPct && (
                  <span className="text-[9px] font-mono font-bold text-void leading-none">
                    {Math.round(widthPct)}%
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
      {isEmpty ? (
        <span className="text-[9px] font-mono text-text-dim">No unit-mix data</span>
      ) : (
        <ul className={clsx('flex flex-wrap font-mono', compact ? 'gap-x-3 gap-y-0.5 text-[9.5px]' : 'gap-x-3 gap-y-1 text-micro')}>
          {rows.map((row, i) => {
            const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
            return (
              <li
                key={`legend-${row.label}-${i}`}
                className="flex items-center gap-1.5 text-text-bright"
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    compact ? 'w-[7px] h-[7px]' : 'w-1.5 h-1.5',
                    'rounded-full',
                    row.color || FALLBACK_COLOR,
                  )}
                />
                <span>{row.label}</span>
                <span className="text-accent tabular-nums font-semibold">{pct}%</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
